"use client";

import React, { useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import sanitizeAndParseInlineHTML from "@/utils/functions/sanitizeAndParseInlineHTML";
import { ctaBackgroundColors } from "../../utils/ctaBackgroundColors";
import { getTypeBackgroundColor } from "../../utils/mapTypeToColor";
import useWebview from "@/webview/useWebview";
import useTranslations from "@/i18n/useTranslations";
import { useLocale } from "next-intl";
import { NewProductCardProps } from "../../types";
import { renderBadges } from "../../utils/renderBadges";
import { getTranslatedProductType } from "@/components/ProductCardList/utils/getTranslatedProductType";
import { useCompletionTimeText } from "../../utils/useCompletionTimeText";
import { useTranslatedLevel } from "../../utils/useTranslatedLevel";
import {
  getBrandStringFromPartnerKeys,
  getTagularSubject,
  setCorrelationId,
} from "@/utils/tagular/helpers";
import {
  FormatSubType,
  FormatType,
  NYI_NUMBER,
  NYI_STRING,
} from "@/constants/tagular/main";
import { ProductClicked } from "@/hooks/eventing/types/redventures.ecommerce.v1.ProductClicked";
import { translateVariant } from "@/utils/tagular/dataTranslationMatrices";
import { createCustomDimensions } from "@/utils/tagular/createDimension";
import useProductEvents from "@/hooks/eventing/useProductEvents";

interface HoverCardProps extends NewProductCardProps {
  hrefWithQueryParams?: string;
}

const getLineHeight = (titleLineCount: number, hasBadges: boolean) => {
  if (!hasBadges) {
    return titleLineCount === 1
      ? "leading-[1.24rem] [&_*]:leading-[1.24rem]"
      : "leading-[1.19rem] [&_*]:leading-[1.19rem]";
  }
  return titleLineCount === 1
    ? "leading-[1.19rem] [&_*]:leading-[1.19rem]"
    : "leading-[1.14rem] [&_*]:leading-[1.14rem]";
};

const getWebkitLineClamp = (
  titleLineCount: number,
  shouldShowCompletionTimeAndLevel: boolean,
) => {
  if (!shouldShowCompletionTimeAndLevel) {
    return titleLineCount === 1 ? 12 : 11;
  }
  return titleLineCount === 1 ? 11 : 10;
};

export function HoverCard(props: HoverCardProps) {
  const t = useTranslations("ProductCompare");
  const tCommon = useTranslations("Common");
  const locale = useLocale();
  const getCompletionTimeText = useCompletionTimeText();
  const translateLevel = useTranslatedLevel();

  const { getUrl } = useWebview();
  const titleRef = useRef<HTMLDivElement>(null);
  const index = props.index || -1;
  const [titleLineCount, setTitleLineCount] = useState(2);
  const containerWrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLastCard, setIsLastCard] = useState(false);

  const ctaTextEnglish = props.hoverEffectCta?.text || "Learn more";
  const ctaText = props.hoverEffectCta?.text || t("learnMore");
  const completionTimeText = getCompletionTimeText(
    props.weeksToComplete,
    false,
  );
  const tagularProduct: ProductClicked["product"] = {
    brand: getBrandStringFromPartnerKeys(
      Array.isArray(props.partnerFullName)
        ? props.partnerFullName
        : [props.partnerFullName ?? ""],
    ),
    formatSubtype: FormatSubType.Grid,
    formatType: FormatType.ProductCardCta,
    location: props.location || NYI_STRING,
    name: props.name,
    variant: props.tagular_variant || translateVariant(props.type),
    position: index < 0 ? NYI_NUMBER : index + 1,
    category: getTagularSubject(props.subjects),
    productId: props.uuid,
    text: ctaTextEnglish,
  };

  const customDimensions: ProductClicked["customDimensions"] =
    createCustomDimensions([
      ["level", props.level],
      ["pacing", props.pacing],
      ["isPartOfProgram", props.isPartOfProgram],
      ["learningType", props.type],
      ["courseCount", props.childCoursesCount],
      ["weeksToComplete", props.weeksToComplete],
      ["searchQueryId", props.searchQueryId],
    ]);

  const { productClicked, correlationId } = useProductEvents({
    product: tagularProduct,
    customDimensions,
  });

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    props.onClick?.(e);
  };

  const handleClickCta = () => {
    setCorrelationId(correlationId);
    productClicked({ listId: props.listId });
  };

  const badges = renderBadges({
    monarchBadges: props.badges,
    type: props.type,
    badgesByProduct: props.badgesByProduct,
    popularItemList: props.popularItemList,
    uuid: props.uuid,
    programType: props.programType,
    microDegreeTag: props.microDegreeTag,
  });

  const colorKey = props.cta?.backgroundColor || "";
  const typeBackgroundColor =
    ctaBackgroundColors[colorKey] ?? getTypeBackgroundColor(props.type);

  const hideCta = props.hoverEffectCta?.hideCta || false;

  useEffect(() => {
    if (titleRef.current) {
      const lineHeight = parseInt(
        getComputedStyle(titleRef.current).lineHeight,
      );
      const height = titleRef.current.offsetHeight;
      const lines = Math.round(height / lineHeight);
      setTitleLineCount(lines);
    }
  }, [props.name, props.partnerFullName]);

  useEffect(() => {
    if (containerWrapperRef.current && containerRef.current) {
      const containerWrapper = containerWrapperRef.current;
      const tooltip = containerRef.current;
      const card = containerWrapper.closest(".group");

      if (card) {
        const handleMouseEnter = () => {
          tooltip.style.opacity = "1";
          tooltip.style.pointerEvents = "auto";
        };

        const handleMouseLeave = () => {
          tooltip.style.opacity = "0";
          tooltip.style.pointerEvents = "none";
        };

        card.addEventListener("mouseenter", handleMouseEnter);
        card.addEventListener("mouseleave", handleMouseLeave);

        return () => {
          card.removeEventListener("mouseenter", handleMouseEnter);
          card.removeEventListener("mouseleave", handleMouseLeave);
        };
      }
    }
  }, []);

  useEffect(() => {
    const checkIfLastCard = () => {
      if (!containerWrapperRef.current) return;

      const cardElement = containerWrapperRef.current.closest(".group");
      if (!cardElement) return;
      const cardRect = cardElement.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const spaceToRight = viewportWidth - cardRect.right;
      const requiredSpace = 302 + 14 + 20;
      const shouldShowLeft = spaceToRight < requiredSpace;
      setIsLastCard(shouldShowLeft);
    };
    const timeoutId = setTimeout(checkIfLastCard, 0);

    window.addEventListener("resize", checkIfLastCard);

    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", checkIfLastCard);
    };
  }, []);

  const showLevel = props.level && props.type !== "Executive Education";
  const translatedLevel = translateLevel(props.level);
  const displayType = getTranslatedProductType(
    props.cta?.topCtaText || props.type,
    locale,
  );
  const shouldShowCompletionTimeAndLevel = Boolean(
    completionTimeText || showLevel,
  );
  const skillsListText = props?.hoverOverlay?.skillsList?.join(", ");

  return (
    <>
      <div
        ref={containerWrapperRef}
        className={`hidden md:block absolute ${isLastCard ? "right-full" : "left-full"} top-0 w-3.5 h-full pointer-events-auto z-10`}
      />
      <div
        ref={containerRef}
        className={`hidden ${isLastCard ? "mr-3.5" : "ml-3.5"} md:block absolute ${isLastCard ? "right-full" : "left-full"} top-1/2 -translate-y-1/2 z-20 bg-white w-[302px] h-[438px] opacity-0 transition-opacity duration-300 pointer-events-none border border-gray-200 rounded-[12px] shadow-product-card`}
      >
        {/* Arrow pointing to card */}
        {isLastCard ? (
          <div className="absolute right-0 top-1/2 translate-x-[15px] -translate-y-1/2">
            <div className="w-0 h-0 border-t-[15px] border-t-transparent border-b-[15px] border-b-transparent border-l-[15px] border-l-gray-200"></div>
            <div className="absolute top-1/2 -translate-y-1/2 w-0 h-0 border-t-[15px] border-t-transparent border-b-[15px] border-b-transparent border-l-[15px] border-l-putty-500"></div>
          </div>
        ) : (
          <div className="absolute left-0 top-1/2 -translate-x-[15px] -translate-y-1/2">
            <div className="w-0 h-0 border-t-[15px] border-t-transparent border-b-[15px] border-b-transparent border-r-[15px] border-r-gray-200"></div>
            <div className="absolute top-1/2 -translate-y-1/2 w-0 h-0 border-t-[15px] border-t-transparent border-b-[15px] border-b-transparent border-r-[15px] border-r-putty-500"></div>
          </div>
        )}

        <div className={`w-full px-4 py-2 rounded-t-xl ${typeBackgroundColor}`}>
          <span className="font-bold text-primary-500 text-sm">
            {displayType}
          </span>
        </div>

        <a
          className="p-4 block h-[438px] overflow-hidden"
          href={getUrl(props.hrefWithQueryParams || props.href)}
          onClick={handleClick}
        >
          <div
            ref={titleRef}
            className="text-base font-bold mb-2 line-clamp-2 overflow-hidden text-ellipsis"
          >
            {`${props.partnerFullName}: ${props.name}`}
          </div>

          <div className="mt-2 mb-4 font-bold text-xs flex flex-row flex-wrap gap-2">
            {props.showBadges && badges.length > 0 && (
              <div className="flex flex-row gap-2 flex-wrap">{badges}</div>
            )}

            {shouldShowCompletionTimeAndLevel && (
              <div className="flex flex-row flex-wrap items-center">
                {completionTimeText && <span>{completionTimeText}</span>}
                {completionTimeText && showLevel && (
                  <span className="mx-1">•</span>
                )}
                {showLevel && <span>{translatedLevel}</span>}
              </div>
            )}
          </div>
          <div
            className={`text-xs ${getLineHeight(titleLineCount, badges.length > 0)} 
          [&_*]:text-xs [&_p]:!my-0 [&_ul]:!px-4 [&_li]:!py-0 flex flex-col gap-2 overflow-hidden`}
          >
            <div
              className="overflow-hidden text-ellipsis"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: getWebkitLineClamp(
                  titleLineCount,
                  shouldShowCompletionTimeAndLevel,
                ),
                WebkitBoxOrient: "vertical",
              }}
            >
              {props.hoverOverlay?.primaryDescription &&
                props.hoverDataControl?.showPrimaryDescription &&
                sanitizeAndParseInlineHTML(
                  props.hoverOverlay.primaryDescription,
                )}
              {props.hoverOverlay?.aboutThisProduct &&
                props.hoverDataControl?.showAboutThisProduct &&
                sanitizeAndParseInlineHTML(props.hoverOverlay.aboutThisProduct)}
              {props.hoverOverlay?.learningBulletItems &&
                props.hoverDataControl?.showLearningBulletItems &&
                sanitizeAndParseInlineHTML(
                  props.hoverOverlay.learningBulletItems,
                )}
              {skillsListText !== undefined &&
                props.hoverDataControl?.showSkillsList &&
                skillsListText.length > 0 && (
                  <p>
                    <b>{tCommon("skillsCovered")}</b>: {skillsListText}
                  </p>
                )}
            </div>
          </div>
        </a>

        {!hideCta && (
          <div className="absolute w-full bottom-0 p-4 bg-white rounded-b-[12px]">
            <Button
              variant="primaryOutline"
              asChild
              className="w-full border-2 font-normal text-primary-500 hover:bg-primary hover:text-white"
            >
              <a
                href={getUrl(props.hrefWithQueryParams || props.href)}
                onClick={handleClickCta}
              >
                {ctaText}
              </a>
            </Button>
          </div>
        )}
      </div>
    </>
  );
}
